/**
 * Der Hinweisstreifen nach dem Archivieren (Spec 02a, „Oberfläche ·
 * Archivieren"): steht auf der Ansicht des Elternobjekts, bis der Nutzer
 * weiternavigiert. Keine Liste, kein Filter — die Rückmeldung derselben
 * Aktion (ADR 0009 §2 bleibt unberührt).
 *
 * `notice` kommt aus `location.state` (`lib/archived-notice.ts`) — verschwindet
 * von selbst, sobald der Nutzer woanders hinnavigiert, weil eine neue
 * Navigation ohne `state` keinen Hinweis mehr trägt.
 */

import { useState } from 'react'

import { ApiError } from '@/lib/api'
import type { ArchivedNotice } from '@/lib/archived-notice'
import { useUnarchiveEntity } from '@/features/objects/api'

interface ObjectArchivedNoticeProps {
  readonly notice: ArchivedNotice | undefined
}

export function ObjectArchivedNotice({ notice }: ObjectArchivedNoticeProps) {
  const unarchiveMutation = useUnarchiveEntity()
  const [isRestored, setIsRestored] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  // Ein neuer Hinweis (anderes `id`, z. B. ein zweites Archivieren in
  // derselben Sitzung ohne Neumontage dieser Komponente) setzt den bereits
  // wiederhergestellten Zustand zurück — während des Renderns verglichen
  // (React-Muster „Adjusting state when a prop changes"), nicht in einem
  // Effekt.
  const [syncedNoticeId, setSyncedNoticeId] = useState(notice?.id)
  if (notice?.id !== syncedNoticeId) {
    setSyncedNoticeId(notice?.id)
    setIsRestored(false)
    setError(undefined)
  }

  if (notice === undefined || isRestored) return null

  async function handleUndo() {
    if (notice === undefined) return
    setError(undefined)
    try {
      await unarchiveMutation.mutateAsync({ kind: notice.kind, id: notice.id, ifMatch: String(notice.version) })
      setIsRestored(true)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <div
      data-testid="object-archived-notice"
      role="status"
      className="border-border bg-bg text-text flex flex-wrap items-center justify-between gap-2 border-b px-(--page-x) py-3"
    >
      <p>„{notice.label}" wurde archiviert.</p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="object-archived-undo"
          disabled={unarchiveMutation.isPending}
          aria-busy={unarchiveMutation.isPending}
          onClick={() => void handleUndo()}
          className="text-accent hover:underline disabled:opacity-60"
        >
          Wiederherstellen
        </button>
        {error !== undefined && (
          <p role="alert" className="text-label text-status-overdue">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
